import { FC, useCallback, useMemo } from 'react';
import { Box, BoxProps, IconButton, SxProps, Theme, useMediaQuery } from '@mui/material';
import { Accept, useDropzone, DropzoneOptions } from 'react-dropzone';
import { FileUploadDto, getBase64Async } from '@sweatpals/shared';
import { clsx } from '../../utils';
import { photoStyles } from './photo.styles';
import { Colors } from '../../constants';
import { CameraIcon, DeleteIcon } from '../../icons';

export interface PhotoProps extends Omit<BoxProps, 'onChange' | 'onError'> {
	circle?: boolean;

	width?: number;
	height?: number;

	maxWidth?: number;
	maxHeight?: number;

	isDeleteButtonHidden?: boolean;

	error?: boolean;

	// fallback content, if value is not provided
	children?: React.ReactNode;

	accept?: Accept;
	maxFileSize?: number;

	withWrapper?: boolean;

	sx?: SxProps<Theme>;
	wrapperSx?: SxProps<Theme>;
	iconSx?: SxProps<Theme>;
	deleteButtonSx?: SxProps<Theme>;
	dropzoneOptions?: DropzoneOptions;

	value?: FileUploadDto | null;
	previousValue?: string | null;
	disabled?: boolean;

	EditButton?: FC<{ open: () => void; disabled?: boolean }>;

	onChange?(newImage: FileUploadDto | null): void;
	onError?(error: Error): void;
}

export const Photo: FC<PhotoProps> = ({
	circle,
	error,
	value,
	accept,
	onChange,
	withWrapper,
	wrapperSx,
	children,
	previousValue,
	disabled,
	width,
	height,
	maxWidth,
	maxHeight,
	iconSx,
	isDeleteButtonHidden,
	EditButton,
	deleteButtonSx,
	dropzoneOptions,
	onError,
	maxFileSize,
	...props
}) => {
	const handleFileDrop = useCallback(
		async (acceptedFiles: File[]): Promise<void> => {
			const firstAcceptedFile = acceptedFiles[0];

			if (!firstAcceptedFile) {
				return;
			}

			const base64String = await getBase64Async(firstAcceptedFile);
			onChange?.({ fileName: firstAcceptedFile.name, base64EncodedFile: base64String, type: 'image' });
		},
		[onChange]
	);

	const { getRootProps, getInputProps, open } = useDropzone({
		accept,
		maxSize: maxFileSize,
		disabled,
		onDrop: handleFileDrop,
		multiple: false,
		onError: (error) => {
			onError?.(error);
			console.error(error);
		},
	});

	const isLessThanSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	return (
		<Box
			{...props}
			{...getRootProps()}
			sx={clsx(
				{
					width: 'fit-content',
					height: 'fit-content',
					borderRadius: circle ? '50%' : '2px',
					position: 'relative',

					'&:hover .delete-photo-btn': {
						display: 'flex',
					},
				},
				wrapperSx
			)}
		>
			{(value || !!previousValue) && !disabled && !isDeleteButtonHidden ? (
				<IconButton
					className='delete-photo-btn'
					sx={clsx(
						{
							display: isLessThanSmall ? 'flex' : 'none',
							width: '40px',
							height: '40px',
							position: 'absolute',
							top: 0,
							right: 0,
							transform: 'translate(50%, 0%)',
							zIndex: 2,
							backgroundColor: Colors.black_a50,

							'&:hover': {
								backgroundColor: Colors.black_a40,
							},
						},
						deleteButtonSx,
					)}
					onClick={(e) => {
						e.stopPropagation();

						onChange?.(null);
					}}
				>
					<Box
						component={DeleteIcon}
						width={20}
						height={20}
						sx={{
							color: Colors.white,
							mixBlendMode: 'difference',
						}}
					/>
				</IconButton>
			) : null}
			{EditButton && (<EditButton open={open} disabled={disabled} />)}

			<input {...getInputProps()} />

			<Box
				sx={clsx(
					photoStyles(
						value?.base64EncodedFile ?? previousValue,
						withWrapper,
						{ width, height, maxWidth, maxHeight },
						circle,
						error,
						disabled
					),
					props.sx
				)}
				className={error ? 'Mui-error' : ''}
			>
				{!value && !previousValue
					? (children ?? <Box component={CameraIcon} sx={clsx({ width: '24px', height: '24px', color: '#6D7275' }, iconSx)} />)
					: null}
			</Box>
		</Box>
	);
};
